import { useRef, useTransition } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue } from "recoil";
import type { Subscription } from "rxjs";

import {
  BookingApi,
  bookingsPollingStateAtom,
  BookingsPollingStatusEnum,
  castErrorResult,
  companyIdSelector,
  createCatchErrorResult,
  createErrorResult,
  createSuccessResultVoid,
  errors,
  isAbortError,
  isCanceledError,
  Log,
  simpleResultErrorEmptyResponse,
  simpleResultInvalidApiResponse,
  warnAndToastSimpleResult,
  type IBookingsPollingAbortPayload,
  type IBookingsPollingCombinedState,
  type IBookingsPollingInitResult,
  type IBulkSetBookingsDto,
  type IBulkSetBookingsInitPayload,
  type IBulkUpdateBookingsDateTimeDto,
  type IBulkUpdateBookingsDateTimeInitPayload,
  type SimpleResult,
} from "@ryde/shared";

import {
  isBookingsPollingCurrentStateNotRunning,
  isBookingsPollingStatusRunning,
} from "./bookings-polling.helpers";

export const useBookingsPollingActions = () => {
  const { t } = useTranslation();
  const abortControllerRef = useRef<AbortController | undefined>(undefined);
  const subscriptionRef = useRef<Subscription | null>(null);
  const [bookingsPollingState, setBookingsPollingState] = useRecoilState(
    bookingsPollingStateAtom,
  );

  const [, startTransition] = useTransition();
  const companyId = useRecoilValue(companyIdSelector);

  /**
   * Both runs drive the same stream: only the endpoint and what gets logged differ, so the whole
   * result-unwrapping ladder and the state hand-off live here once.
   */
  const startPollingRun = useEvent(
    async <TPayload>({
      payload,
      logLabel,
      callApi,
    }: {
      payload: TPayload;
      logLabel: string;
      callApi: (
        body: TPayload & { companyId: number },
      ) => Promise<SimpleResult<IBookingsPollingInitResult> | undefined>;
    }): Promise<SimpleResult<IBookingsPollingInitResult>> => {
      try {
        Log.debug(logLabel, payload);

        if (!companyId) {
          return createErrorResult(errors.common.invalidCompanyId);
        }

        const pollingStartResult = await callApi({ ...payload, companyId });

        if (!pollingStartResult) {
          return simpleResultErrorEmptyResponse(t);
        }

        if (!pollingStartResult.success) {
          return pollingStartResult;
        }

        if (!pollingStartResult.data) {
          return simpleResultInvalidApiResponse(t);
        }

        const { token, state } = pollingStartResult.data;

        if (!token || !state) {
          return createErrorResult(
            errors.produce.missingToken,
            "Missing booking polling state or ID",
          );
        }

        setBookingsPollingState(state);

        return pollingStartResult;
      } catch (error) {
        Log.warn(t(errors.booking.errorOccurredWhenUpdatingBooking), error);
        return createCatchErrorResult(error, t);
      }
    },
  );

  const bookingsPollingStart = useEvent(
    (
      payload: IBulkSetBookingsDto,
    ): Promise<SimpleResult<IBookingsPollingInitResult>> =>
      startPollingRun({
        payload,
        logLabel: "Init bookings polling for payload:",
        callApi: body =>
          BookingApi.bookingsPollingStart({
            body: body as IBulkSetBookingsInitPayload,
          }),
      }),
  );

  const bookingsDateTimeUpdatePollingStart = useEvent(
    (
      payload: IBulkUpdateBookingsDateTimeDto,
    ): Promise<SimpleResult<IBookingsPollingInitResult>> =>
      startPollingRun({
        payload,
        logLabel: "Init bookings date and time update polling:",
        callApi: body =>
          BookingApi.bookingsDateTimeUpdatePollingStart({
            body: body as IBulkUpdateBookingsDateTimeInitPayload,
          }),
      }),
  );

  const apiAbortBookingsPollingIfRunning = useEvent(
    async (): Promise<SimpleResult<void>> => {
      if (!bookingsPollingState?.token) {
        Log.debug("No active  booking create to abort");
        return createSuccessResultVoid();
      }

      const isProductionInProgress =
        isBookingsPollingStatusRunning(bookingsPollingState);

      if (!isProductionInProgress) {
        Log.debug("No active  booking create to abort");
        return createSuccessResultVoid();
      }

      const payload: IBookingsPollingAbortPayload = {
        token: bookingsPollingState?.token,
      };

      Log.debug(
        "Bookings abort api payload",
        payload,
        "state",
        bookingsPollingState,
      );
      const apiResult = await BookingApi.bookingsPollingAbort({
        body: payload,
      });

      if (!apiResult) {
        return simpleResultErrorEmptyResponse(t);
      }

      if (!apiResult.success) {
        return castErrorResult(apiResult);
      }

      if (apiResult.success && !apiResult.data) {
        return simpleResultInvalidApiResponse(t);
      }

      return createSuccessResultVoid();
    },
  );

  const stopAndCleanupBookingsPolling = useEvent(
    async ({
      updatedState,
    }: {
      updatedState?: IBookingsPollingCombinedState;
    } = {}): Promise<SimpleResult<void>> => {
      try {
        const stateComputed = updatedState ?? bookingsPollingState;
        Log.debug(
          "Stopping bookings polling, state ",
          bookingsPollingState,
          "override",
          updatedState,
        );

        if (isBookingsPollingStatusRunning(stateComputed)) {
          Log.debug(
            "Previous bookings polling not finished, aborting it-",
            bookingsPollingState,
          );
          const abortBookingPollingResult =
            await apiAbortBookingsPollingIfRunning();

          if (!abortBookingPollingResult.success) {
            warnAndToastSimpleResult(abortBookingPollingResult, t);
            return abortBookingPollingResult;
          }
        }

        return createSuccessResultVoid();
      } catch (error) {
        return createCatchErrorResult(error, t);
      } finally {
        if (!subscriptionRef.current?.closed) {
          Log.debug(`Stop bookings polling`);
          subscriptionRef.current?.unsubscribe();
        }

        subscriptionRef.current = null;
      }
    },
  );

  const apiCheckBookingsPollingState = useEvent(
    async (abortController?: AbortController) => {
      try {
        if (isBookingsPollingCurrentStateNotRunning(bookingsPollingState)) {
          await stopAndCleanupBookingsPolling({
            updatedState: bookingsPollingState,
          });
          return;
        }

        if (!bookingsPollingState?.token) {
          Log.warn(
            "Missing booking polling token data on polling check id, state:",
            bookingsPollingState,
          );
          stopAndCleanupBookingsPolling();
          return;
        }

        const payload: IBookingsPollingAbortPayload = {
          token: bookingsPollingState?.token,
        };

        Log.debug(
          "Check booking polling status, payload:",
          payload,
          "state:",
          bookingsPollingState,
        );
        const checkBookingsPollingStatusResult =
          await BookingApi.bookingsPollingCheckStatus({
            body: payload,
            abortController,
          });

        if (
          !checkBookingsPollingStatusResult ||
          (checkBookingsPollingStatusResult.success &&
            !checkBookingsPollingStatusResult.data)
        ) {
          Log.warn(
            "Invalid polling check result from api",
            checkBookingsPollingStatusResult,
          );
          stopAndCleanupBookingsPolling();
          return;
        }

        const checkedState = checkBookingsPollingStatusResult.data?.state;

        if (!checkedState) {
          Log.debug("No state returned from polling iteration, skipping check");
          return;
        }

        if (checkedState.status === BookingsPollingStatusEnum.Aborted) {
          Log.infoAndToast(
            t(errors.booking.bookingCreationProcessCanceledByUser),
          );
        }

        setBookingsPollingState(checkedState);

        if (isBookingsPollingCurrentStateNotRunning(checkedState)) {
          stopAndCleanupBookingsPolling({ updatedState: checkedState });
        }
      } catch (error) {
        if (isAbortError(error)) {
          Log.debug(
            "Skip a tick checking booking create status, overlapped polling tick",
            error,
          );
        } else if (isCanceledError(error)) {
          Log.debug(
            "Skip a tick checking booking create status, canceled polling tick from BE",
            error,
          );
        } else {
          Log.error("Error checking booking create status", error);
        }
      }
    },
  );

  const backgroundCheckBookingsPolling = useEvent(() => {
    const prevController = abortControllerRef.current;
    const newController = new AbortController();
    abortControllerRef.current = newController;

    if (prevController) {
      Log.debug("Abort previous bookings check check on next");
      prevController.abort();
    }

    startTransition(() => {
      apiCheckBookingsPollingState(abortControllerRef.current)
        .finally(() => {
          abortControllerRef.current = undefined;
        })
        .catch(error => {
          Log.warn("Error in polling iteration", error);
        });
    });
  });
  return {
    bookingsPollingStart,
    bookingsDateTimeUpdatePollingStart,
    backgroundCheckBookingsPolling,
    subscriptionRef,
    stopAndCleanupBookingsPolling,
    apiAbortBookingsPollingIfRunning,
    apiCheckBookingsPollingState,
  };
};
